import React from "react";
import { Download } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import PageShell, { PageHead, RichText } from "@/components/pages/PageShell";

const UPDATED = new Date(2026, 5, 1);

const Contents = ({ sections, label }) => (
  <nav aria-label={label} data-testid="page-contents" className="hidden lg:block lg:col-span-3 lg:sticky lg:top-28 self-start">
    <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-amber">{label}</div>
    <ol className="mt-4 space-y-2.5 border-l border-line pl-4">
      {sections.map((s, i) => (
        <li key={s.h}>
          <a href={`#s-${i}`} className="text-sm font-bold text-ink-muted hover:text-moss transition-colors">{s.h}</a>
        </li>
      ))}
    </ol>
  </nav>
);

const Block = ({ s, i }) => (
  <section id={`s-${i}`} data-testid={`page-section-${i}`} className="py-8 first:pt-0">
    <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-ink">{s.h}</h2>
    {s.p?.map((para) => (
      <p key={para.slice(0, 32)} className="mt-3 text-base text-ink-muted leading-relaxed">
        <RichText text={para} />
      </p>
    ))}
    {s.list && (
      <ul className="mt-4 grid sm:grid-cols-2 gap-3">
        {s.list.map((item) => (
          <li key={item} className="journal-card px-4 py-3 text-sm font-bold text-ink flex gap-3">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-amber shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default function InfoPage({ page }) {
  const { t, locale } = useLang();
  const p = t.pages[page];
  const legal = page === "privacy" || page === "terms";
  const months = t.pages.common.months;
  const updated = months
    ? `${months[UPDATED.getMonth()]} ${UPDATED.getFullYear()}`
    : new Intl.DateTimeFormat([locale, "nb-NO"], { month: "long", year: "numeric" }).format(UPDATED);
  return (
    <PageShell testId={`${page}-page`}>
      <PageHead kicker={p.kicker} title={p.title} lead={p.lead}>
        {legal && (
          <div data-testid="page-updated" className="mt-6 inline-flex rounded-full border border-dashed border-line px-4 py-1.5 text-sm font-bold text-ink-muted">
            {t.pages.common.updated}: {updated}
          </div>
        )}
      </PageHead>
      <div className="grid lg:grid-cols-12 gap-10 mt-14 md:mt-20">
        <Contents sections={p.sections} label={t.pages.common.contents} />
        <div className="lg:col-span-8 lg:col-start-5 divide-y divide-dashed divide-line">
          {p.sections.map((s, i) => <Block key={s.h} s={s} i={i} />)}
          {page === "press" && (
            <div className="pt-8">
              <a
                href="/brand/logo-src.png"
                download="trollfossjakten-logo.png"
                data-testid="press-logo-download"
                className="inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-6 py-3 font-extrabold transition-colors"
              >
                <Download className="w-4 h-4" />
                {p.asset}
              </a>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
